import { useState, useMemo } from 'react';
import { Box, Switch, IconButton, Typography, Tooltip, Chip, Button } from '@mui/material';
import { IconEdit, IconTrash, IconRefresh } from '@tabler/icons';
import {
    GridColDef,
    GridToolbarContainer,
    GridToolbarFilterButton,
    useGridApiRef,
    useGridApiContext,
    GridFilterModel
} from '@mui/x-data-grid-pro';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import { FilterPanel, FilterPanelProps } from 'ui-component/records/RecordGrid/components';
import { SearchBox } from 'ui-component/records/GridOptions/components';
import { StripedDataGrid } from 'views/backoffice/CustomLists/components';
import { Notification } from '../../types';
import { frequencyDisplayNames } from '../../mocks/notificationData';

interface NotificationGridProps {
    notifications: Notification[];
    loading: boolean;
    showDeleted: boolean;
    setShowDeleted: (show: boolean) => void;
    onEdit: (notification: Notification) => void;
    onDelete: (id: string) => void;
    onRestore: (id: string) => void;
    onToggle: (id: string, enabled: boolean) => void;
    onCreate: () => void;
    toggling: boolean;
    restoring: boolean;
}

interface CustomToolbarProps {
    showDeleted: boolean;
    setShowDeleted: (show: boolean) => void;
    onCreate: () => void;
}

const CustomToolbar = ({ showDeleted, setShowDeleted, onCreate }: CustomToolbarProps) => {
    const apiRef = useGridApiContext();
    const [searchInput, setSearchInput] = useState('');

    const handleChangeSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newVal = e.target.value;
        setSearchInput(newVal);
        apiRef.current.setQuickFilterValues([newVal]);
    };

    return (
        <GridToolbarContainer data-testid="notification-grid-toolbar" sx={{ p: 2, gap: 2, borderBottom: 'none' }}>
            <Box sx={{ width: '40%' }}>
                <SearchBox
                    data-testid="notification-grid-search"
                    fullWidth
                    value={searchInput}
                    onChange={handleChangeSearch}
                    onClear={() => {
                        setSearchInput('');
                        apiRef.current.setQuickFilterValues([]);
                    }}
                    placeholder="Find in current page"
                />
            </Box>

            <GridToolbarFilterButton data-testid="notification-grid-filter-button" />

            <Button
                data-testid="notification-grid-toggle-deleted-button"
                startIcon={<IconTrash size={18} />}
                onClick={() => setShowDeleted(!showDeleted)}
                size="small"
                sx={{
                    color: 'primary.main',
                    backgroundColor: '#F5F6F7',
                    textTransform: 'none',
                    '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                    }
                }}
            >
                {showDeleted ? 'Hide Deleted' : 'Show Deleted'}
            </Button>

            <Button
                data-testid="notification-grid-create-button"
                sx={{
                    ml: 'auto',
                    textTransform: 'none',
                    color: 'primary.main',
                    backgroundColor: '#F5F6F7',
                    '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                    }
                }}
                onClick={onCreate}
                size="small"
                variant="text"
            >
                Create
            </Button>
        </GridToolbarContainer>
    );
};

const NotificationGrid = ({
    notifications,
    loading,
    showDeleted,
    setShowDeleted,
    onEdit,
    onDelete,
    onRestore,
    onToggle,
    onCreate,
    toggling,
    restoring
}: NotificationGridProps) => {
    const apiRef = useGridApiRef();
    const [newFilterModel, setNewFilterModel] = useState<GridFilterModel>();
    const [appliedFilterModel, setAppliedFilterModel] = useState<GridFilterModel>();

    const isSameFilterModel = useMemo(
        () => isEqual(omit(newFilterModel, ['id']), omit(appliedFilterModel, ['id'])),
        [newFilterModel, appliedFilterModel]
    );

    const filteredNotifications = useMemo(
        () => notifications.filter((n) => (showDeleted ? true : !n.deleted)),
        [notifications, showDeleted]
    );

    const handleFilterChange = (model: GridFilterModel) => {
        setNewFilterModel(model);
        if (model.items.length === 0) {
            setAppliedFilterModel(model);
        }
    };

    const handleClickApply = () => {
        setAppliedFilterModel(newFilterModel);
    };

    const CustomToolbarWithProps = () => <CustomToolbar showDeleted={showDeleted} setShowDeleted={setShowDeleted} onCreate={onCreate} />;

    const columns: GridColDef[] = [
        {
            field: 'enabled',
            headerName: '',
            width: 80,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            hideable: false,
            renderCell: (params) => (
                <Switch
                    data-testid={`notification-enabled-switch-${params.row.id}`}
                    checked={params.value}
                    onChange={(e) => onToggle(params.row.id, e.target.checked)}
                    disabled={params.row.deleted || toggling}
                    size="small"
                    sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                            color: 'primary.main',
                            transition: 'color 0.2s ease'
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: 'primary.main',
                            transition: 'background-color 0.2s ease'
                        },
                        '& .MuiSwitch-switchBase': {
                            color: 'grey.600',
                            transition: 'color 0.2s ease'
                        },
                        '.MuiDataGrid-row:hover &': {
                            '& .MuiSwitch-switchBase.Mui-checked': {
                                color: 'primary.dark'
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: 'primary.dark'
                            },
                            '& .MuiSwitch-switchBase:not(.Mui-checked)': {
                                color: 'grey.700'
                            }
                        }
                    }}
                />
            )
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 120,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            hideable: false,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title="Edit">
                        <span>
                            <IconButton
                                data-testid={`notification-edit-button-${params.row.id}`}
                                size="small"
                                onClick={() => onEdit(params.row)}
                                disabled={params.row.deleted}
                                sx={{
                                    transition: 'color 0.2s ease',
                                    '.MuiDataGrid-row:hover &': {
                                        color: 'primary.dark'
                                    }
                                }}
                            >
                                <IconEdit size={16} />
                            </IconButton>
                        </span>
                    </Tooltip>
                    {params.row.deleted ? (
                        <Tooltip title="Restore">
                            <IconButton
                                data-testid={`notification-restore-button-${params.row.id}`}
                                size="small"
                                onClick={() => onRestore(params.row.id)}
                                disabled={restoring}
                                className="restore-button"
                                sx={{
                                    opacity: '1 !important',
                                    transition: 'color 0.2s ease'
                                }}
                            >
                                <IconRefresh size={16} />
                            </IconButton>
                        </Tooltip>
                    ) : (
                        <Tooltip title="Delete">
                            <IconButton
                                data-testid={`notification-delete-button-${params.row.id}`}
                                size="small"
                                onClick={() => onDelete(params.row.id)}
                                sx={{
                                    transition: 'color 0.2s ease',
                                    '.MuiDataGrid-row &': {
                                        color: 'error.dark'
                                    }
                                }}
                            >
                                <IconTrash size={16} />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            )
        },
        {
            field: 'recordTypeName',
            headerName: 'Record Type',
            width: 200,
            valueGetter: (params) => params.row?.recordType?.displayName || params.row?.recordType?.name || '',
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography>{params.value || ''}</Typography>
                    {params.row.deleted && <Chip onClick={() => {}} label="Deleted" size="small" color="error" variant="outlined" />}
                </Box>
            )
        },
        {
            field: 'event',
            headerName: 'Trigger/event',
            width: 180,
            valueGetter: (params) => params.row?.event?.displayName || '',
            renderCell: (params) => <Typography>{params.row.event?.displayName || ''}</Typography>
        },
        {
            field: 'priority',
            headerName: 'Priority',
            width: 130,
            type: 'singleSelect',
            valueOptions: ['urgent', 'important', 'regular'],
            renderCell: (params) => <Typography sx={{ textTransform: 'capitalize' }}>{params.value}</Typography>
        },
        {
            field: 'frequency',
            headerName: 'Delivery',
            width: 130,
            type: 'singleSelect',
            valueOptions: ['real_time', '10m', '30m', '60m', 'daily', 'weekly'],
            renderCell: (params) => (
                <Typography>
                    {frequencyDisplayNames[params.row.frequency as keyof typeof frequencyDisplayNames] || params.row.frequency}
                </Typography>
            )
        },
        {
            field: 'channels',
            headerName: 'Channels',
            width: 130,
            sortable: false,
            filterable: false,
            renderCell: () => <Typography>In App</Typography>
        },
        {
            field: 'condition',
            headerName: 'Condition',
            width: 150,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                <Chip
                    label={params.row.enabled ? 'Active' : 'Inactive'}
                    size="small"
                    color={params.row.enabled ? 'success' : 'error'}
                    variant="outlined"
                />
            )
        },
        {
            field: 'settings',
            headerName: 'Settings',
            width: 150,
            sortable: false,
            filterable: false,
            renderCell: (params) => <Typography>{params.row.condition?.fieldName || '-'}</Typography>
        }
    ];

    return (
        <Box data-testid="notification-grid-container" sx={{ height: '100%', width: '100%' }}>
            <StripedDataGrid
                data-testid="notification-grid"
                apiRef={apiRef}
                rows={filteredNotifications}
                columns={columns}
                loading={loading}
                localeText={{
                    noRowsLabel: 'Please create a notification'
                }}
                getRowId={(row) => row.id}
                getRowClassName={(params) => {
                    const baseClass = params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd';
                    return params.row.deleted ? `${baseClass} deleted-row` : baseClass;
                }}
                onFilterModelChange={handleFilterChange}
                disableRowSelectionOnClick
                density="compact"
                pinnedColumns={{ left: ['enabled', 'actions'] }}
                slots={{
                    toolbar: CustomToolbarWithProps,
                    filterPanel: FilterPanel
                }}
                slotProps={{
                    filterPanel: {
                        isApplied: isSameFilterModel,
                        isLoading: loading,
                        onClickApply: handleClickApply,
                        disableApply: false,
                        filterModel: newFilterModel
                    } as FilterPanelProps
                }}
                sx={{
                    '& .MuiDataGrid-columnHeaderTitle': {
                        fontWeight: 700
                    },
                    '& .MuiDataGrid-row': {
                        transition: 'background-color 0.2s ease',
                        '&:hover': {
                            backgroundColor: 'grey.200',
                            '& .MuiTypography-root': {
                                color: 'primary.dark'
                            },
                            '& .restore-button': {
                                color: 'primary.dark'
                            }
                        }
                    },
                    '& .deleted-row': {
                        '& .restore-button:hover': {
                            color: 'primary.dark'
                        },
                        '& .MuiTypography-root': {
                            opacity: 0.5
                        },
                        '& .MuiSwitch-root': {
                            opacity: 0.3
                        },
                        '& .MuiIconButton-root': {
                            opacity: 0.4
                        },
                        '& .MuiChip-root': {
                            opacity: 0.8
                        },
                        '&:hover': {
                            '& .MuiTypography-root': {
                                color: 'text.disabled'
                            }
                        }
                    }
                }}
            />
        </Box>
    );
};

export default NotificationGrid;
